import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import Image from 'next/image'
import { checkConcurrentStream } from '../../actions/concurrentStreamActions'
import styles from './styles'

const RETRY_TIMEOUT = 5000

const ConcurrentStreamNotice = ({
  onSuccess,
  redirectUrl,
  renderMode = 'modal',
  channel,
  sportsExclusive,
}) => {
  const dispatch = useDispatch()
  const { isChecking, error } = useSelector(state => state.concurrentStream)
  const [retryDisabled, setRetryDisabled] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)

  useEffect(() => {
    let timer
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(time => time - 1)
      }, 1000)
    } else if (timeLeft === 0) {
      setRetryDisabled(false)
    }

    return () => {
      if (timer) {
        clearInterval(timer)
      }
    }
  }, [timeLeft])

  const handleRetry = async () => {
    setRetryDisabled(true)
    setTimeLeft(Math.floor(RETRY_TIMEOUT / 1000))

    // eslint-disable-next-line max-len
    const hasConcurrent = await dispatch(checkConcurrentStream(false, channel, sportsExclusive, true))

    if (!hasConcurrent) {
      if (onSuccess) {
        onSuccess()
      }

      // Only redirect if a redirectUrl is provided and it's different from current page
      if (redirectUrl && redirectUrl !== window.location.href) {
        window.location.href = redirectUrl
      }
    }
  }

  const content = (
    <div className={`concurrent-stream-notice ${renderMode}`}>
      <div className="device-image-outer">
        <Image
          src={require('./ConcurrentStreamNotice-illustration.png')}
          alt="Illustration of multiple devices displaying sports streams"
          width={406}
          height={213}
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      </div>
      <h2>You can only stream on one device at a time</h2>
      <p>To watch here, please stop streaming on any other device.</p>
      <button
        type="button"
        onClick={handleRetry}
        className={`retry-button ${isChecking || retryDisabled ? 'retrying' : ''}`}
        disabled={isChecking || retryDisabled}
      >
        { /* eslint-disable-next-line no-nested-ternary */ }
        {isChecking ? 'Checking...' : retryDisabled ? `Retry in ${timeLeft}s` : 'Retry'}
      </button>
      {error && (
        <p className="error-message">
          Unable to check stream status. Please refresh and try again.
        </p>
      )}
      <style jsx>{styles}</style>
    </div>
  )

  return content
}

ConcurrentStreamNotice.propTypes = {
  onSuccess: PropTypes.func,
  redirectUrl: PropTypes.string,
  renderMode: PropTypes.oneOf(['modal', 'inline']),
  channel: PropTypes.string,
  sportsExclusive: PropTypes.bool,
}

ConcurrentStreamNotice.defaultProps = {
  onSuccess: () => {},
  redirectUrl: '',
  renderMode: 'modal',
  channel: '',
  sportsExclusive: false,
}

export default ConcurrentStreamNotice
